import axios from "axios";
import authService from "./authService";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";

const API_BASE = `${BACKEND_URL}/stocks`;

/**
 * Stock Service
 * Gerencia todas as operações de estoque
 */
class StockService {
  /**
   * Get authorization headers with JWT token
   * @returns {object} Headers object
   */
  getAuthHeaders() {
    const token = authService.getToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  }

  /**
   * Get all stocks
   * @returns {Promise<Array>}
   */
  async getAllStocks() {
    try {
      const response = await axios.get(API_BASE, this.getAuthHeaders());
      return response.data?.data || response.data || [];
    } catch (error) {
      if (error.response?.status === 404) {
        return [];
      }
      console.error("❌ Erro ao buscar estoque:", error);
      throw error;
    }
  }

  /**
   * Get stock by ID
   * @param {number} id - Stock ID
   * @returns {Promise<object>}
   */
  async getStockById(id) {
    try {
      const response = await axios.get(
        `${API_BASE}/${id}`,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao buscar estoque ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new stock entry
   * @param {object} stockData - Stock data
   * @returns {Promise<object>}
   */
  async createStock(stockData) {
    try {
      // Validar dados antes de enviar
      this.validateStockData(stockData);

      const response = await axios.post(
        API_BASE,
        stockData,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("❌ Erro ao criar entrada de estoque:", error);
      throw error;
    }
  }

  /**
   * Update stock entry
   * @param {number} id - Stock ID
   * @param {object} stockData - Updated stock data
   * @returns {Promise<object>}
   */
  async updateStock(id, stockData) {
    try {
      // Validar dados antes de enviar (não exigir product_id na atualização)
      this.validateStockData(stockData, true);

      const response = await axios.put(
        `${API_BASE}/${id}`,
        stockData,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao atualizar estoque ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete stock entry
   * @param {number} id - Stock ID
   * @returns {Promise<void>}
   */
  async deleteStock(id) {
    try {
      await axios.delete(`${API_BASE}/${id}`, this.getAuthHeaders());
    } catch (error) {
      console.error(`❌ Erro ao deletar estoque ${id}:`, error);
      throw error;
    }
  }

  /**
   * Validate stock data
   * @param {object} data - Stock data to validate
   * @param {boolean} isUpdate - If true, product_id is optional
   * @throws {Error} If validation fails
   */
  validateStockData(data, isUpdate = false) {
    const errors = [];

    // product_id é obrigatório apenas na criação
    if (!isUpdate && !data.product_id) {
      errors.push("Produto é obrigatório");
    }

    if (!data.storage_location_id) {
      errors.push("Local de armazenamento é obrigatório");
    }

    if (!data.lot_code || data.lot_code.trim() === "") {
      errors.push("Código do lote é obrigatório");
    }

    if (
      data.current_quantity === null ||
      data.current_quantity === undefined ||
      data.current_quantity < 0
    ) {
      errors.push("Quantidade deve ser maior ou igual a zero");
    }

    if (
      data.unit_cost === null ||
      data.unit_cost === undefined ||
      data.unit_cost <= 0
    ) {
      errors.push("Custo unitário deve ser maior que zero");
    }

    if (!data.manufacture_date) {
      errors.push("Data de fabricação é obrigatória");
    }

    if (!data.expiration_date) {
      errors.push("Data de validade é obrigatória");
    }

    // Validar se data de validade é posterior à data de fabricação
    if (data.manufacture_date && data.expiration_date) {
      const manufactureDate = new Date(data.manufacture_date);
      const expirationDate = new Date(data.expiration_date);

      if (expirationDate <= manufactureDate) {
        errors.push("Data de validade deve ser posterior à data de fabricação");
      }
    }

    if (errors.length > 0) {
      throw new Error(errors.join(", "));
    }
  }

  /**
   * Calculate available quantity (current - reserved)
   * @param {number} currentQuantity - Current quantity
   * @param {number} reservedQuantity - Reserved quantity
   * @returns {number}
   */
  calculateAvailableQuantity(currentQuantity, reservedQuantity) {
    return (currentQuantity || 0) - (reservedQuantity || 0);
  }

  /**
   * Calculate days until expiration
   * @param {string} expirationDate - Expiration date (ISO format)
   * @returns {number}
   */
  calculateDaysUntilExpiration(expirationDate) {
    if (!expirationDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expDate = new Date(expirationDate);
    expDate.setHours(0, 0, 0, 0);

    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  /**
   * Get expiration status
   * @param {string} expirationDate - Expiration date (ISO format)
   * @returns {object} Status with color and label
   */
  getExpirationStatus(expirationDate) {
    const days = this.calculateDaysUntilExpiration(expirationDate);

    if (days === null) {
      return { color: "gray", label: "Sem data", variant: "secondary" };
    }

    if (days < 0) {
      return { color: "red", label: "Vencido", variant: "destructive" };
    }

    if (days === 0) {
      return { color: "red", label: "Vence hoje", variant: "destructive" };
    }

    if (days <= 7) {
      return {
        color: "red",
        label: `${days}d restante${days > 1 ? "s" : ""}`,
        variant: "destructive",
      };
    }

    if (days <= 30) {
      return {
        color: "yellow",
        label: `${days}d restante${days > 1 ? "s" : ""}`,
        variant: "warning",
      };
    }

    return {
      color: "green",
      label: `${days}d restante${days > 1 ? "s" : ""}`,
      variant: "default",
    };
  }

  /**
   * Get stock status based on quantity
   * @param {number} currentQuantity - Current quantity
   * @param {number} reservedQuantity - Reserved quantity
   * @returns {object} Status with color and label
   */
  getStockStatus(currentQuantity, reservedQuantity) {
    const available = this.calculateAvailableQuantity(
      currentQuantity,
      reservedQuantity
    );

    if (available <= 0) {
      return { color: "red", label: "Esgotado", variant: "destructive" };
    }

    if (available <= 10) {
      return { color: "yellow", label: "Baixo", variant: "warning" };
    }

    return { color: "green", label: "Disponível", variant: "success" };
  }

  /**
   * Format currency to BRL
   * @param {number} value - Value to format
   * @returns {string}
   */
  formatCurrency(value) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  }

  /**
   * Format date to Brazilian format
   * @param {string} date - ISO date string
   * @returns {string}
   */
  formatDate(date) {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("pt-BR");
  }

  /**
   * Calculate total stock value
   * @param {number} quantity - Quantity
   * @param {number} unitCost - Unit cost
   * @returns {number}
   */
  calculateTotalValue(quantity, unitCost) {
    return (quantity || 0) * (unitCost || 0);
  }
}

export default new StockService();
